import "./style.scss";
import Avatar from "@mui/material/Avatar";
import React, { useState, forwardRef, useImperativeHandle, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import NativeSelect from "@mui/material/NativeSelect";
import { FormattedMessage } from "react-intl";
import { changeLanguage } from "../../stores/language/langSlice";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate, Link } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { ROOT_API } from "../../api/config";
import { useIntl } from "react-intl";
import { ErrorCode } from "../../api/ErrorCode";
import CircularProgress from "@mui/material/CircularProgress";
import { clearUserInformation } from "../../stores/users/userSlice";

const changePasswordSchema = yup.object().shape({
  old_password: yup
    .string()
    .required(<FormattedMessage id="password_not_blank" />),
  new_password: yup
    .string()
    .required(<FormattedMessage id="password_not_blank" />),
  confirm_new_password: yup
    .string()
    .oneOf(
      [yup.ref("new_password")],
      <FormattedMessage id="confirm_password_not_correct" />
    ),
});

const ChangePassword = forwardRef((props, ref) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    mode: "onSubmit",
    resolver: yupResolver(changePasswordSchema),
  });

  useImperativeHandle(ref, () => ({
    handleClose,
    handleOpen,
  }));

  const intl = useIntl();

  const [open, setOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  useImperativeHandle(ref, () => ({
    handleClose,
    handleOpen,
  }));

  const selectedLocale =
    useSelector((state) => state.lang.value) ||
    localStorage.getItem("lang") ||
    "hr";

  const onSubmit = async (data) => {
    const axiosObject = axios.create({
      headers: {
        "Content-Type": "application/json",
        lang: selectedLocale,
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      },
    });
    setIsLoading(true);
    try {
      const res = await axiosObject.post(
        `${ROOT_API}apis/change-password`,
        data
      );
      handleClose();
      if (res) {
        Swal.fire({
          icon: "success",
          title: intl.formatMessage({
            id: "change_password_success",
          }),
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      handleClose();
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text:
          error &&
          error.response &&
          error.response.data &&
          error.response.data.error_code
            ? intl.formatMessage({
                id: ErrorCode[error.response.data.error_code],
              })
            : intl.formatMessage({
                id: "there_are_error_occured",
              }),
      });
    }
    setIsLoading(false);
  };

  return (
    <div>
      <Dialog open={open} onClose={handleClose} sx={{ margin: "0px" }}>
        <DialogTitle>
          {intl.formatMessage({ id: "change_password" })}
        </DialogTitle>
        <DialogContent>
          <form
            className="pt-36"
            onSubmit={handleSubmit(onSubmit)}
            style={{ width: "400px" }}
          >
            <div className="general-form-main-form_group mb-12">
              <input
                className="px-10 py-12 radius-4 font-16"
                placeholder={intl.formatMessage({ id: "old_password" })}
                type="password"
                {...register("old_password")}
              />
              {errors.old_password && (
                <p className="error font-14 mt-2">
                  {errors.old_password.message}
                </p>
              )}
            </div>
            <div className="general-form-main-form_group mb-12">
              <input
                className="px-10 py-12 radius-4 font-16"
                placeholder={intl.formatMessage({
                  id: "new_password",
                })}
                type="password"
                {...register("new_password")}
              />
              {errors.new_password && (
                <p className="error font-14 mt-2">
                  {errors.new_password.message}
                </p>
              )}
            </div>
            <div className="general-form-main-form_group mb-12">
              <input
                className="px-10 py-12 radius-4 font-16"
                placeholder={intl.formatMessage({
                  id: "confirm_new_password",
                })}
                type="password"
                {...register("confirm_new_password")}
              />
              {errors.confirm_new_password && (
                <p className="error font-14 mt-2">
                  {errors.confirm_new_password.message}
                </p>
              )}
            </div>
          </form>
        </DialogContent>
        <Box
          sx={{
            display: isLoading ? "flex" : "none",
            justifyContent: "center",
            marginBottom: "6px",
          }}
        >
          <CircularProgress />
        </Box>
        <DialogActions style={{ marginBottom: "12px", padding: "0px 24px" }}>
          <button
            className="btn-cta"
            style={{ width: "100%" }}
            type="submit"
            onClick={handleSubmit(onSubmit)}
          >
            {intl.formatMessage({ id: "submit" })}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
});

const Header = (props) => {
  const changePasswordRef = useRef();

  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);

  const lang =
    useSelector((state) => state.lang.value) ||
    localStorage.getItem("lang") ||
    "hr";

  const access_token = localStorage.getItem("access_token");

  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const navigation = useNavigate();

  const switchLanguage = (newLanguage) => {
    localStorage.setItem("lang", newLanguage);
    dispatch(changeLanguage(newLanguage));
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    dispatch(clearUserInformation());
    navigation("/login");
  };

  const handleChangePassword = () => {
    changePasswordRef.current.handleOpen();
  };

  return (
    <header className="header">
      <div className="container">
        <div className="header-main px-0 py-20 flex justify-between align-center">
          <Link className="header-wordmark" to={access_token ? "/pocetna" : "/"}>
            <img
              className="header-wordmark-mark"
              src={`${process.env.PUBLIC_URL}/assets/mascot/beaver-head-trim.png`}
              alt=""
              aria-hidden="true"
            />
            nemastruje
            <span className="header-wordmark-dot" aria-hidden="true" />
          </Link>
          <div className="header-main_right flex gap-20 align-center">
            <FormControl fullWidth>
              <NativeSelect
                defaultValue={lang}
                inputProps={{
                  name: "language",
                  id: "uncontrolled-native",
                }}
                variant="outlined"
                sx={{
                  width: "120px",
                  color: "var(--moon)",
                  "& .MuiNativeSelect-icon": { color: "var(--moon)" },
                  "&::before": {
                    borderBottomColor: "rgba(174, 185, 204, 0.4)",
                  },
                  "&:hover:not(.Mui-disabled, .Mui-error)::before": {
                    borderBottomColor: "var(--moon)",
                  },
                }}
                onChange={(e) => {
                  switchLanguage(e.target.value);
                }}
              >
                <option value="en">
                  <FormattedMessage id="english" />
                </option>
                <option value="hr">
                  <FormattedMessage id="croatian" />
                </option>
              </NativeSelect>
            </FormControl>
            {access_token &&
            user &&
            Object.keys(user).length > 0 &&
            user.full_name ? (
              <div className="pointer">
                <Avatar
                  sx={{ bgcolor: "var(--tungsten-deep)", color: "#21150a" }}
                  id="basic-button"
                  aria-controls={open ? "basic-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  onClick={handleClick}
                >
                  {user.full_name[0]}
                </Avatar>
              </div>
            ) : null}
          </div>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <MenuItem
              onClick={() => {
                handleClose();
                navigation("/moj-racun");
              }}
            >
              <FormattedMessage id="my_account" />
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <FormattedMessage id="logout" />
            </MenuItem>
          </Menu>
        </div>
        <ChangePassword ref={changePasswordRef} />
      </div>
    </header>
  );
};

export default Header;
